import { createFileRoute, Link } from "@tanstack/react-router"
import { AlertTriangleIcon, CircleCheck, Loader2, MailMinusIcon } from "lucide-react"
import { z } from "zod"

import { getTurnstilePublicKey, Turnstile } from "@/cloudflare/turnstile"
import { useFormAction } from "@/form"
import { Alert, AlertDescription } from "@/ui/alert"
import { Button } from "@/ui/button"
import { Input } from "@/ui/input"
import { Link as UiLink } from "@/ui/link"
import { Heading, Muted } from "@/ui/typography"

import { unsubscribeFormAction, unsubscribeFormSchema } from "../rpc"

const unsubscribeSearchSchema = z.object({
  email: z.string().catch(""),
})

export const Route = createFileRoute("/newsletter/unsubscribe")({
  validateSearch: unsubscribeSearchSchema,
  loader: () => getTurnstilePublicKey(),
  component: Unsubscribe,
})

function Unsubscribe() {
  const { email } = Route.useSearch()
  const { cloudflareTurnstilePublicKey } = Route.useLoaderData()
  const { data, error, isPending, onSubmit } = useFormAction(
    unsubscribeFormSchema,
    unsubscribeFormAction,
  )
  const { success } = data || {}
  const turnstileSubjectKey = isPending ? "pending" : "idle"

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="container mx-auto flex min-h-screen flex-col items-center justify-center gap-6 px-4"
    >
      <div className="max-w-md space-y-6 text-center">
        <MailMinusIcon className="mx-auto size-10" />
        <div>
          <Heading level={2}>Unsubscribe from the newsletter</Heading>
          <Muted>No login required. This takes effect immediately.</Muted>
        </div>

        {error?.message && (
          <Alert variant="destructive">
            <AlertTriangleIcon />
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert>
            <CircleCheck />
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        {!success && (
          <div className="space-y-4">
            <Input
              name="email"
              type="email"
              autoComplete="email"
              defaultValue={email}
              placeholder="you@example.com"
              required
              disabled={isPending}
            />
            {cloudflareTurnstilePublicKey ? (
              <Turnstile siteKey={cloudflareTurnstilePublicKey} subjectKey={turnstileSubjectKey} />
            ) : null}
            <Button type="submit" disabled={isPending}>
              {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              Unsubscribe
            </Button>
          </div>
        )}

        <p>
          <UiLink size="sm" asChild className="text-muted-foreground">
            <Link to="/">Back to home</Link>
          </UiLink>
        </p>
      </div>
    </form>
  )
}
